// src/app/culinary-partners/[partnerId]/page.jsx
import React from "react";
import { culinaryPartners } from "../../../lib/data";
import { Card, CardHeader, CardTitle, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import {
  ArrowLeft,
  ChefHat,
  CakeSlice,
  Cookie,
  Cake,
  Candy,
  Sandwich,
  Fish,
  Salad,
  Utensils
} from "lucide-react";

export default function CulinaryPartnerDetailPage({ partnerId }) {
  const partner = culinaryPartners.find((p) => p.id === partnerId);

  if (!partner) {
    return (
      <div className="min-h-screen p-6 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-4">Partner Not Found</h2>
          <Button onClick={() => window.location.hash = '#/culinary-partners'}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Partners
          </Button>
        </div>
      </div>
    );
  }

  const getOfferingIcon = (name) => {
    if (name.includes("Cake")) return CakeSlice;
    if (name.includes("Macaron")) return Cookie;
    if (name.includes("Cupcake")) return Cake;
    if (name.includes("Chocolate")) return Candy;
    if (name.includes("Burger")) return Sandwich;
    if (name.includes("Sushi")) return Fish;
    if (name.includes("Pasta")) return Salad;
    return Utensils;
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          className="mb-6"
          onClick={() => window.location.hash = '#/culinary-partners'}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Partners
        </Button>

        {/* Partner Header */}
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center">
              <ChefHat className="h-10 w-10 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                {partner.name}
              </h1>
              <p className="text-xl text-muted-foreground mt-2">
                {partner.description}
              </p>
            </div>
          </div>
          <Badge className="bg-primary text-primary-foreground text-lg px-4 py-1">
            {partner.specialty} Specialist
          </Badge>
        </div>

        {/* Offerings Grid */}
        <div>
          <h2 className="text-3xl font-bold mb-6">Menu Offerings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {partner.offerings?.map((offering) => (
              <Card
                key={offering.id}
                className="group overflow-hidden hover:shadow-2xl transition-all duration-300 backdrop-blur-sm bg-card/70 border-2 border-transparent hover:border-border"
              >
                <div className="aspect-square bg-gradient-to-br from-secondary/60 to-accent/40 flex items-center justify-center">
                  {(() => {
                    const Icon = getOfferingIcon(offering.name);
                    return <Icon className="h-16 w-16 text-primary/50" />;
                  })()}
                </div>
                <CardHeader>
                  <CardTitle className="text-xl group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-primary group-hover:to-accent group-hover:bg-clip-text transition-all duration-300">
                    {offering.name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    {offering.description}
                  </p>
                  <Button
                    size="sm"
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/30"
                    onClick={() => window.location.hash = '#/customization'}
                  >
                    Customize
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
